import { REGION_ORDER } from '../constants/regions';
import type { SequenceData } from '../types';

/**
 * Converts a max amino-acid length and a region count into a pixel column width.
 *
 *
 *   10px  × maxLength   — approximate width per amino acid character
 *  + 2px  × numRegions  — left + right padding per region block
 *  + 5px  × (numRegions - 1) — gap between each pair of adjacent region blocks
 *  + 32px               — fixed overhead: borders, scrollbar, container padding
 *
 * Both the per-clonotype (InnerChildSequenceTable) and the global across-all-
 * clonotypes (useGlobalChainWidths) calculations use this function so
 * stays in one place.
 */
export function calcSequenceColumnWidth(maxLength: number, numRegions: number): number {
  return maxLength * 10 + numRegions * 2 + Math.max(0, numRegions - 1) * 5 + 32;
}

/**
 * Computes the pixel width budget for a heavy or light chain column within a
 * single clonotype's sequences.
 *
 * Width is calculated from ALL regions (max possible) so the column never
 * resizes when Show/Hide FWR is toggled — only the cell content changes.
 *
 * @param sequences    - All sequences in the clonotype
 * @param chainAccessor - Returns the chain (heavy or light) for a given sequence
 */
export function calcChainWidth(sequences: SequenceData[], chainAccessor: (seq: SequenceData) => SequenceData['heavyChain']): number {
  const seqs = sequences.filter((seq) => chainAccessor(seq));

  if (seqs.length === 0) {
    return 0;
  }

  const maxLength = Math.max(
    ...seqs.map((seq) => {
      const chain = chainAccessor(seq);
      if (!chain) {
        return 0;
      }
      if (!chain.regions) {
        return chain.sequence.length;
      }
      return REGION_ORDER.reduce((total, { key }) => {
        const regionSeq = chain.regions[key as keyof typeof chain.regions];
        return total + (regionSeq?.length || 0);
      }, 0);
    }),
    0,
  );

  // All 7 regions always contribute to the width budget so toggling
  // Show/Hide FWR never resizes the column — only the cell content changes.
  return calcSequenceColumnWidth(maxLength, REGION_ORDER.length);
}
